import { Section } from '../../sections/builders/section_builder';

import {
  ISection,
  IRegistrationSection,
  IPusherOutcome,
  IRegistrationWaitlistPusherOutcome
} from '../../../interfaces/scheduler_interfaces';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { RegistrationAlertsServant } from './registration_alerts_servant';
import { ScheduledSectionServant } from './scheduled_section_servant';
import { RequisiteCourseServant } from './requisite_course_servant';
import { IRegisterSectionsDatasource } from '../datasources/register_sections_datasource';
import { RegistrationFailureModel } from '../models/registration_failure_model';
import { RegisterSectionsQuery } from '../queries/register_sections_query';

import * as _4 from 'lodash4';
import { ICommand } from 'core-ts-types';

export class RegistrationCallbackServant {
  constructor(
    private ngSectionsDatasource: ISectionsDatasource,
    private ngRegistrationAlertsServant: RegistrationAlertsServant,
    private ngRequisiteCourseServant: RequisiteCourseServant,
    private ngRegisterSectionsDatasource: IRegisterSectionsDatasource,
    private ngRegistrationFailureModel: RegistrationFailureModel,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngRegisterSectionsQuery: RegisterSectionsQuery,
    private updateSeatAvailabilityCommand: ICommand
  ) {}

  setRegisteredStatus(successfulSectionOutcome: IRegistrationWaitlistPusherOutcome): void {
    // pusher returns successful registration
    const section = this.ngSectionsDatasource.get(successfulSectionOutcome.id);
    if (section) {
      section.status = ScheduledSectionStatus.REGISTERED;
      this.ngSectionsDatasource.put(section);
    }

    this.removeSectionError(successfulSectionOutcome.id);
  }

  removeSectionError(sectionId: number) {
    this.ngRegistrationFailureModel.del(sectionId);
  }

  showSuccessfulRegistrationAlerts(successfulRegistrationsOutcome: IPusherOutcome[]) {
    const hasSuccessfulRegistrations = successfulRegistrationsOutcome.length > 0;
    if (hasSuccessfulRegistrations) {
      this.ngRegistrationAlertsServant.showSuccessRegistrationAlerts(successfulRegistrationsOutcome);
    }
    const getRequisitesForAlert = (regOutcome: any[]) => {
      const sectionRequisites = _4.map(regOutcome, (registrationOutcome: IPusherOutcome) => {
        const sectionIdFromOutcome = registrationOutcome.id;
        const matchingFullSection = _4.find(
          this.ngRegisterSectionsDatasource.values(),
          (section: IRegistrationSection) => _4.isEqual(section.id, sectionIdFromOutcome)
        );
        return this.ngRequisiteCourseServant.getRequisitesForSection(sectionIdFromOutcome, matchingFullSection._course);
      });
      return _4.filter(
        sectionRequisites,
        (sectionRequisite: any) =>
          !_4.isEmpty(sectionRequisite.missing_prereqs) || !_4.isEmpty(sectionRequisite.missing_coreqs)
      );
    };
    const requisitesForAlert = hasSuccessfulRegistrations ? getRequisitesForAlert(successfulRegistrationsOutcome) : [];
    if (!_4.isEmpty(requisitesForAlert)) {
      this.ngRegistrationAlertsServant.showHasRequisitesRegistrationAlerts(requisitesForAlert);
    }
  }

  showAutoRegisteredAlert(successfulRegistrationsOutcome: IPusherOutcome) {
    this.ngRegistrationAlertsServant.showAutoWaitlistSectionRegisteredAlerts(successfulRegistrationsOutcome);
  }

  handlePusherFailedResponse(
    failedRegistrationsOutcome: IPusherOutcome[],
    sectionStatusesBeforeRegistration: any
  ): void {
    // Get updated seats for all failed sections
    const sectionIds = _4.map(failedRegistrationsOutcome, (sectionOutcome: IPusherOutcome) => sectionOutcome.id);
    this.updateSeatAvailabilityCommand(sectionIds);

    // pusher returns failed registration
    _4.each(failedRegistrationsOutcome, (sectionOutcome: IPusherOutcome) => {
      const section = this.ngSectionsDatasource.get(sectionOutcome.id);

      const isAutoWaitlistStatus = section ? _4.get(section, '_availability.is_auto_waitlist', false) : false;
      if (isAutoWaitlistStatus) {
        if (section) {
          section.status = ScheduledSectionStatus.WAITLISTED;
          this.ngSectionsDatasource.put(section);
        }
      } else {
        if (section) {
          section.status = sectionStatusesBeforeRegistration[section.id];
          this.ngSectionsDatasource.put(section);
        }
        this.ngRegistrationFailureModel.put({
          type: 'ADD',
          id: sectionOutcome.id,
          message: sectionOutcome.message
        });
      }
    });
  }

  showFailedRegistrationAlerts(failedRegistrationsOutcome: IPusherOutcome[], selectedSections: ISection[]) {
    const auto_waitlist_sections = this.ngScheduledSectionServant.getAutoWaitlistSections(selectedSections);
    // show failed alerts
    if (failedRegistrationsOutcome.length > 0) {
      if (_4.size(auto_waitlist_sections) > 0) {
        const sectionRegistrationOutcomesByAutoWailist = _4.partition(
          failedRegistrationsOutcome,
          (sectionRegistrationOutc: IPusherOutcome) => _4.includes(auto_waitlist_sections, sectionRegistrationOutc.id)
        );
        const autoWaitlistOutcome = _4.get(sectionRegistrationOutcomesByAutoWailist, '[0]', []);
        const nonAutoWaitlistOutcome = _4.get(sectionRegistrationOutcomesByAutoWailist, '[1]', []);
        // autowaitlist outcome
        _4.each(sectionRegistrationOutcomesByAutoWailist[0], (autoWaitlistedSection: IPusherOutcome) => {
          // TODO: verify pusher outcome to get position and pass it to method // There are 3 students ahead of you. We will automatically register you when a seat becomes available.
          this.ngRegistrationAlertsServant.showAutoWaitlistSectionWaitlistedAlerts(autoWaitlistedSection);
        });
        // non autowaitlist outcome
        if (_4.size(sectionRegistrationOutcomesByAutoWailist[1]) > 0) {
          this.ngRegistrationAlertsServant.showFailedRegistrationAlerts(sectionRegistrationOutcomesByAutoWailist[1]);
        }
      } else {
        this.ngRegistrationAlertsServant.showFailedRegistrationAlerts(failedRegistrationsOutcome);
      }
    }
  }

  showRegistrationAutoWaitlistedAlerts(autowaitlistedSections: any) {
    _4.each(autowaitlistedSections, (autoWaitlistedSection: IPusherOutcome) => {
      // TODO: verify pusher outcome to get position and pass it to method // There are 3 students ahead of you. We will automatically register you when a seat becomes available.
      this.ngRegistrationAlertsServant.showAutoWaitlistSectionWaitlistedAlerts(autoWaitlistedSection);
    });
  }

  showEnforcedCoreqsDropReminderAlerts(successfulRegistrationsOutcome: IPusherOutcome[]) {
    const hasSuccessfulRegistrations = successfulRegistrationsOutcome.length > 0;
    const getSectionsWithRegisteredCoreqs = (regOutcome: IPusherOutcome[]): IRegistrationSection[] => {
      const sectionsWithCoreqsRegistered: IRegistrationSection[] = [];
      _4.forEach(regOutcome, (registrationOutcome: IPusherOutcome) => {
        const sectionIdFromOutcome = registrationOutcome.id;
        const matchingFullSection = this.ngRegisterSectionsDatasource.get(sectionIdFromOutcome);
        if (matchingFullSection) {
          const sectionRegisteredCoreqs = this.ngScheduledSectionServant.getRegisteredCoreqsRelated(
            matchingFullSection,
            this.ngRegisterSectionsDatasource.values()
          );
          if (sectionRegisteredCoreqs.length > 0) {
            sectionsWithCoreqsRegistered.push(matchingFullSection);
          }
        }
      });
      return _4.filter(sectionsWithCoreqsRegistered, !_4.isEmpty);
    };
    const sectionsWithCoreqs: IRegistrationSection[] = hasSuccessfulRegistrations
      ? getSectionsWithRegisteredCoreqs(successfulRegistrationsOutcome)
      : [];
    if (!_4.isEmpty(sectionsWithCoreqs)) {
      this.ngRegistrationAlertsServant.showEnforcedCoreqsDropReminder(sectionsWithCoreqs);
    }
  }
}

angular.module('capability-ng-scheduler').service('ngRegistrationCallbackServant', RegistrationCallbackServant);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/registration_callback_servant.ts